<script lang="ts">
  import { onMount, tick, untrack } from 'svelte';
  import { matchesDimensionFilters } from '../analytics/flow';
  import type { DataStore } from '../data/store';
  import type { DimensionFilters, Id, IsoDate, YearMonth } from '../data/types';
  import { formatCents } from '../presentation/format';
  import type { AppState } from '../state/app.svelte';
  import Button from '../ui/Button.svelte';
  import Icon from '../ui/Icon.svelte';
  import MultiSelectFilter from './MultiSelectFilter.svelte';
  import PeriodPicker from './PeriodPicker.svelte';

  let { appState, store }: { appState: AppState; store: DataStore } = $props();
  let closeButton = $state<HTMLButtonElement>();
  let sheetElement = $state<HTMLDivElement>();
  let liveMode = $state(window.matchMedia('(min-width: 721px)').matches);
  let draftAccounts = $state<Id[]>([]);
  let draftCategories = $state<Id[]>([]);
  let draftFromMonth = $state<YearMonth | null>(null);
  let draftToMonth = $state<YearMonth | null>(null);
  let draftFromDate = $state<IsoDate | null>(null);
  let draftToDate = $state<IsoDate | null>(null);
  let draftUncategorized = $state(false);
  let error = $state('');

  const accountItems = $derived(
    store.core.accounts
      .filter((item) => store.core.account_month.some((row) => row.account_id === item.id))
      .map((item) => ({ id: item.id, label: item.name })),
  );
  const categoryById = $derived(new Map(store.core.categories.map((item) => [item.id, item])));
  const incomeCategoryItems = $derived(categoryItems('income'));
  const expenseCategoryItems = $derived(categoryItems('expense'));
  const allCategoryItems = $derived([...incomeCategoryItems, ...expenseCategoryItems]);
  const preview = $derived.by(() => {
    const accountIds = new Set(draftAccounts);
    const categoryFilterActive = draftCategories.length !== allCategoryItems.length;
    const categoriesAffectAccountMovements = appState.route === 'accounts' && categoryFilterActive;
    if (appState.filterMode === 'stock' && !categoriesAffectAccountMovements) {
      const month = draftToMonth ?? store.months.at(-1);
      const rows = store.core.account_month.filter(
        (row) => row.month === month && accountIds.has(row.account_id),
      );
      return {
        label: `${rows.length} ${rows.length === 1 ? 'conto' : 'conti'} nello scope`,
        value: formatCents(rows.reduce((sum, row) => sum + row.closing_balance_cents, 0)),
      };
    }

    const categoryIds = new Set(draftCategories);
    const dimensionFilters: DimensionFilters = {
      accountIds,
      ...(categoryFilterActive ? { categoryIds, includeUncategorized: draftUncategorized } : {}),
    };
    const accountMovementFrom =
      appState.route === 'accounts'
        ? store.core.calendar.find((day) => !draftFromMonth || day.month >= draftFromMonth)?.date
        : undefined;
    const accountMovementTo =
      appState.route === 'accounts'
        ? store.core.calendar.filter((day) => !draftToMonth || day.month <= draftToMonth).at(-1)
            ?.date
        : undefined;
    const rows = store.transactions.filter((row) => {
      const from = accountMovementFrom ?? draftFromDate;
      const to = accountMovementTo ?? draftToDate;
      if (from && row.date < from) return false;
      if (to && row.date > to) return false;
      if (!matchesDimensionFilters(row, dimensionFilters)) return false;
      if (appState.route === 'expenses' && row.flow !== 'expense') return false;
      if (appState.route === 'income' && row.flow !== 'income') return false;
      return true;
    });
    return {
      label: `${rows.length} ${rows.length === 1 ? 'movimento' : 'movimenti'}${
        appState.route === 'accounts' ? ' nelle categorie' : ' nello scope'
      }`,
      value: formatCents(rows.reduce((sum, row) => sum + row.amount_cents, 0)),
    };
  });

  function categoryItems(kind: 'income' | 'expense') {
    return store.core.categories
      .filter((item) => item.kind === kind)
      .map((item) => ({
        id: item.id,
        label: item.parent_id
          ? `${categoryById.get(item.parent_id)?.name ?? 'Categoria'} › ${item.name}`
          : item.name,
      }));
  }

  function updateCategoryKind(items: ReadonlyArray<{ id: Id }>, selectedForKind: Id[]): void {
    const kindIds = new Set(items.map((item) => item.id));
    draftCategories = [...draftCategories.filter((id) => !kindIds.has(id)), ...selectedForKind];
    commitLive();
  }

  onMount(() => {
    const query = window.matchMedia('(min-width: 721px)');
    const updateMode = () => (liveMode = query.matches);
    query.addEventListener('change', updateMode);
    return () => query.removeEventListener('change', updateMode);
  });

  $effect(() => {
    if (!appState.filterSheetOpen) return;
    untrack(() => {
      draftAccounts = appState.accountFilterActive
        ? [...appState.accountIds]
        : accountItems.map((item) => item.id);
      draftCategories = appState.categoryFilterActive
        ? [...appState.categoryIds]
        : allCategoryItems.map((item) => item.id);
      draftFromMonth = appState.fromMonth;
      draftToMonth = appState.toMonth;
      draftFromDate = appState.fromDate;
      draftToDate = appState.toDate;
      draftUncategorized = appState.includeUncategorized;
      error = '';
    });
    document.documentElement.classList.add('overlay-open');
    void tick().then(() => closeButton?.focus());
    return () => document.documentElement.classList.remove('overlay-open');
  });

  function close(): void {
    appState.filterSheetOpen = false;
  }

  function commit(): boolean {
    if (appState.filterMode === 'stock') {
      if (!draftFromMonth || !draftToMonth || draftFromMonth > draftToMonth) {
        error = 'Inserisci un intervallo mensile valido.';
        return false;
      }
    } else if (!draftFromDate || !draftToDate || draftFromDate > draftToDate) {
      error = 'Inserisci un intervallo di date valido.';
      return false;
    }
    error = '';
    appState.accountFilterActive = draftAccounts.length !== accountItems.length;
    appState.accountIds = appState.accountFilterActive ? draftAccounts : [];
    appState.categoryFilterActive = draftCategories.length !== allCategoryItems.length;
    appState.categoryIds = appState.categoryFilterActive ? draftCategories : [];
    appState.fromMonth = draftFromMonth;
    appState.toMonth = draftToMonth;
    appState.fromDate = draftFromDate;
    appState.toDate = draftToDate;
    appState.includeUncategorized = appState.categoryFilterActive && draftUncategorized;
    appState.syncUrl();
    return true;
  }

  function commitLive(): void {
    if (liveMode) commit();
  }

  function updateDraftPeriod(from: string, to: string): void {
    if (appState.filterMode === 'stock') {
      draftFromMonth = from as YearMonth;
      draftToMonth = to as YearMonth;
    } else {
      draftFromDate = from as IsoDate;
      draftToDate = to as IsoDate;
    }
    commitLive();
  }

  function apply(): void {
    if (commit()) close();
  }

  function reset(): void {
    draftFromMonth = appState.minMonth;
    draftToMonth = appState.maxMonth;
    draftFromDate = appState.minDate;
    draftToDate = appState.maxDate;
    draftAccounts = accountItems.map((item) => item.id);
    draftCategories = allCategoryItems.map((item) => item.id);
    draftUncategorized = false;
    commitLive();
  }

  function onKeydown(event: KeyboardEvent): void {
    if (!appState.filterSheetOpen) return;
    if (event.key === 'Escape') {
      close();
      return;
    }
    if (liveMode || event.key !== 'Tab' || !sheetElement) return;
    const focusable = Array.from(
      sheetElement.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex="0"]',
      ),
    );
    const first = focusable[0];
    const last = focusable.at(-1);
    if (!first || !last) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
</script>

<svelte:window onkeydown={onKeydown} />
{#if appState.filterSheetOpen}
  <div
    class:live-inspector={liveMode}
    class="overlay"
    role="presentation"
    onclick={(event) => !liveMode && event.target === event.currentTarget && close()}
  >
    <div
      bind:this={sheetElement}
      class="filter-sheet"
      role="dialog"
      aria-modal={liveMode ? undefined : 'true'}
      aria-labelledby="filter-title"
      aria-describedby="filter-preview"
    >
      <header class="sheet-header">
        <div>
          <p class="eyebrow">Ambito dashboard</p>
          <h2 id="filter-title">Filtri</h2>
        </div>
        <button
          bind:this={closeButton}
          class="icon-button"
          type="button"
          aria-label="Chiudi filtri"
          onclick={close}
        >
          <Icon name="close" />
        </button>
      </header>

      <div class="sheet-body">
        <fieldset class="period-fields">
          <legend>Periodo</legend>
          <PeriodPicker
            variant="sheet"
            mode={appState.filterMode === 'stock' ? 'month' : 'date'}
            from={appState.filterMode === 'stock' ? draftFromMonth : draftFromDate}
            to={appState.filterMode === 'stock' ? draftToMonth : draftToDate}
            min={(appState.filterMode === 'stock' ? appState.minMonth : appState.minDate) ?? ''}
            max={(appState.filterMode === 'stock' ? appState.maxMonth : appState.maxDate) ?? ''}
            onChange={updateDraftPeriod}
          />
        </fieldset>
        <aside class="filter-preview" id="filter-preview" aria-live="polite">
          <span>{liveMode ? 'Risultato aggiornato' : 'Anteprima del risultato'}</span>
          <strong>{preview.value}</strong>
          <small>{preview.label}</small>
        </aside>
        <MultiSelectFilter
          label="Conti"
          items={accountItems}
          selected={draftAccounts}
          onChange={(value) => {
            draftAccounts = value;
            commitLive();
          }}
        />
        {#if appState.categoryFiltersAvailable}
          <MultiSelectFilter
            label="Categorie entrate"
            items={incomeCategoryItems}
            selected={draftCategories.filter((id) =>
              incomeCategoryItems.some((item) => item.id === id),
            )}
            onChange={(value) => updateCategoryKind(incomeCategoryItems, value)}
          />
          <MultiSelectFilter
            label="Categorie uscite"
            items={expenseCategoryItems}
            selected={draftCategories.filter((id) =>
              expenseCategoryItems.some((item) => item.id === id),
            )}
            onChange={(value) => updateCategoryKind(expenseCategoryItems, value)}
          />
          <label class="check-row"
            ><input type="checkbox" bind:checked={draftUncategorized} onchange={commitLive} /> Includi
            movimenti senza categoria</label
          >
        {/if}
        {#if error}<p class="field-error" role="alert">{error}</p>{/if}
      </div>

      <footer class="sheet-actions">
        <Button variant="ghost" onclick={reset}>Ripristina</Button>
        {#if liveMode}
          <div class="live-filter-status">
            <span aria-hidden="true"></span> Aggiornamento immediato
          </div>
          <Button variant="primary" onclick={close}>Fatto</Button>
        {:else}
          <div class="action-cluster">
            <Button onclick={close}>Annulla</Button><Button variant="primary" onclick={apply}
              >Applica</Button
            >
          </div>
        {/if}
      </footer>
    </div>
  </div>
{/if}
